/* eslint-disable no-console */
import { reactive, type App } from 'vue'
import createAuth0Client from '@auth0/auth0-spa-js'
import type {
  Auth0Client,
  Auth0ClientOptions,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
} from '@auth0/auth0-spa-js'
import { jwtDecode } from 'jwt-decode'
import { getAppMetadata } from '@/plan/metadata/metadata'
import authService, { NO_TOKEN_FOUND } from '@/api/authService'
import AuditService from '@/services/audit/audit.service'
import { Audit, Auth } from '@workspaces/types'
import { getCarto3ApiTokenFromLocalStorage } from '@/helpers/testing/testing.helper'
import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { AuthManager } from '@workspaces/services'
import BrowserCartoAuthorizer from '@/auth/carto-auth'
import { initializeUserPermissionManager } from '@/helpers/auth-browser.helper'
import CartoMetricEvents from '@/services/carto-metric-event.service'
import { subscribeToPlans } from '@/helpers/plans.helper'
import type { Auth0ServiceInstance } from '@/auth/index.type'
import type BrowserEnvironmentResolverType from '@/helpers/environment.helper'
import type { SimpleAuditEvent } from '@/services/audit/audit.type'

interface UseAuth0Options {
  redirectUri?: string
  clientId?: string
  domain?: string
  [key: string]: unknown
}

const maxLoginRetries = 3
let loginRetries = 0

// deck.gl 9 removed the global `setDefaultCredentials` that used to carry the
// share token. In shared-plan mode the Auth0 plugin is never installed, so the
// reactive auth instance does not exist; every CARTO source now reads its token
// through `AuthManager.getToken()` -> `getAccessTokenCartoV3()`. This
// module-level slot lets the anonymous shared-plan path return the share token.
let sharedPlanTokenCartoV3: string | undefined

export function setSharedPlanTokenCartoV3(token: string): void {
  sharedPlanTokenCartoV3 = token
}

export function getSharedPlanTokenCartoV3(): string | undefined {
  return sharedPlanTokenCartoV3
}

/** Creates the reactive Auth0 wrapper (replaces the Vue 2 `new Vue({...})` store). */
function createAuth0Instance(redirectUri: string, options: UseAuth0Options) {
  return reactive({
    loading: false,
    isUserAuthenticated: false,
    user: {} as unknown,
    auth0Client: null as Auth0Client | null,
    error: null as unknown,
    accessTokenCartoV3: 'Waitting for token to be set',
    userCountries: [] as number[],
    role: 'unset' as Auth.Role | string,
    cartoUser: 'unset',
    accountId: 'unset',

    /** Handles the callback when logging in using a redirect */
    async handleRedirectCallback(url?: string) {
      // auth0Client is set in init() before any callback runs.
      const auth0Client = this.auth0Client
      if (!auth0Client) {
        throw new Error('Auth0 client is not initialized')
      }
      if (loginRetries >= maxLoginRetries) {
        console.error(
          'Max login retries reached, loggin out and cleanning all application data',
        )
        auth0Client.logout()
        authService.removeAllApplicationData()
      }
      loginRetries++

      this.loading = true
      try {
        // If the user is returning to the app after   authentication..
        // handle the redirect and retrieve tokens
        await auth0Client.handleRedirectCallback(url)
        this.error = null
      } catch (e) {
        this.error = e
      } finally {
        // Initialize our internal authentication state
        this.isUserAuthenticated = await auth0Client.isAuthenticated()
        const token = await auth0Client.getTokenSilently()
        console.debug('🟧 Auth0: User token', { token })
        if (authService.isWrongToken(token)) {
          authService.setFirstLogin(true)
          console.debug(
            '🟧 Auth0: User does not has organization, assigning organization...',
            { token },
          )
          await authService.joinInviteUserToOrganization(token)
          console.debug('🟧 Auth0: Logging out and redirecting to login...')
          auth0Client.logout()

          authService.removeAllApplicationData()
          auth0Client.loginWithRedirect()
          // eslint-disable-next-line no-unsafe-finally
          return
        }
        // Audit event to register login
        this.setAccessTokenCartoV3(token)
        const isFirstLogin = authService.isFirstLogin()
        const authManager = AuthManager.getInstance()
        authManager.init(new BrowserCartoAuthorizer())
        const type = isFirstLogin
          ? Audit.AuditEventType.LoginFirstTime
          : Audit.AuditEventType.Login
        AuditService.createEvent(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
          // Login events carry no params; getParams() short-circuits.
          { type } as SimpleAuditEvent,
        )
        authService.setFirstLogin(false)
        CartoMetricEvents.sendLoginEvent(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
        )
        await initializeUserPermissionManager()

        subscribeToPlans(
          getAppMetadata(),
          // getInstance() is typed as the EnvironmentResolver interface but
          // returns a real BrowserEnvironmentResolver instance at runtime.
          BrowserEnvironmentResolver.getInstance() as BrowserEnvironmentResolverType,
        )

        this.loading = false
      }
    },
    /** Authenticates the user using the redirect method */
    loginWithRedirect() {
      return this.auth0Client?.loginWithRedirect()
    },
    /** Returns all the claims present in the ID token */
    getIdTokenClaims(o?: GetIdTokenClaimsOptions) {
      return this.auth0Client?.getIdTokenClaims(o)
    },
    /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
    getTokenSilently(o?: GetTokenSilentlyOptions) {
      return this.auth0Client?.getTokenSilently(o)
    },
    /** Logs the user out and removes their session on the authorization server */
    logout() {
      return this.auth0Client?.logout()
    },
    setAccessTokenCartoV3(token: string) {
      this.accessTokenCartoV3 = token
      if (token !== NO_TOKEN_FOUND) {
        const tokenInfo = jwtDecode(token) as {
          sub?: string
          [key: string]: unknown
        }
        this.user = tokenInfo[getAppMetadata().auth0_user_key]
        // sub and the account id claim are strings in the CARTO JWT.
        this.cartoUser = tokenInfo.sub as string
        this.accountId = tokenInfo[
          getAppMetadata().auth0_account_id_key
        ] as string
        // deck.gl 9 removed setDefaultCredentials; CARTO sources now read the
        // token inline at creation time via AuthManager -> getAccessTokenCartoV3.
      }
    },
    getCartoUser() {
      return this.cartoUser
    },
    getAccountId() {
      return this.accountId
    },
    getAccessTokenCartoV3() {
      // console.log('Asking for carto3 token')

      // Anonymous shared plan: there is no logged-in user, so the share token
      // (stored via setSharedPlanTokenCartoV3) is the only credential the CARTO
      // sources can use; return it before the /login redirect below.
      if (sharedPlanTokenCartoV3) {
        return sharedPlanTokenCartoV3
      }

      // TODO: Playwrigth - Find a better way of doing this
      const testingToken = getCarto3ApiTokenFromLocalStorage(
        BrowserEnvironmentResolver.getInstance(),
      )
      if (testingToken) {
        console.log('👿 Testing time, returning testing token', testingToken)
        return testingToken
      }

      const token = this.accessTokenCartoV3
      // console.log('Carto3 token:', token)
      if (
        token === undefined ||
        token === NO_TOKEN_FOUND ||
        token === 'Waitting for token to be set'
      ) {
        console.log('No carto3 token found, redirecting to login')
        /* try {
            const auth0Token = useAuth0?.getAuthCarto3()
            if (auth0Token === undefined || auth0Token === NO_TOKEN_FOUND) {
              window.location.href = `${window.location.origin}/login`
            }
          } catch (e) {
            console.error(e)
            window.location.href = `${window.location.origin}/login`
          } */
        // this.loginWithRedirect()
        window.location.href = `${window.location.origin}/login`
      } else {
        // console.log('Carto 3 token found. Token:', token)
        return token
      }
    },
    setRole(role: Auth.Role | string) {
      this.role = role
    },
    isAdmin() {
      return this.role === Auth.Role.Admin
    },
    isPowerUser() {
      return this.role === Auth.Role.PowerUser
    },
    getUser() {
      return this.user
    },
    getRole() {
      return this.role
    },
    setUserCountries(countries: number[]) {
      this.userCountries = countries
    },
    getUserCountries() {
      return this.userCountries
    },

    /** Instantiate the SDK client (was the Vue 2 `created` lifecycle hook). */
    async init() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        ...options,
        client_id: options.clientId,
        redirect_uri: redirectUri,
      } as Auth0ClientOptions)
      console.debug('🟧 Auth0: Created client', this.auth0Client)
    },
  })
}

let instance: ReturnType<typeof createAuth0Instance> | undefined

/** Returns the current instance of the SDK.
 * The reactive instance exposes the Auth0ServiceInstance surface via its state +
 * methods; cast through unknown since the inferred type can't express it. The
 * instance is always installed at app startup before any consumer calls this. */
export const getInstance = (): Auth0ServiceInstance =>
  instance as unknown as Auth0ServiceInstance

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
const useAuth0 = ({
  redirectUri = `${window.location.origin}/login`,
  ...options
}: UseAuth0Options): ReturnType<typeof createAuth0Instance> => {
  if (instance) return instance

  instance = createAuth0Instance(redirectUri, options)
  // Kick off client creation (the former `created` hook was likewise not awaited).
  instance.init()

  return instance
}

// Vue 3 plugin to expose the wrapper object throughout the application.
export const Auth0Plugin = {
  install(app: App, options: UseAuth0Options) {
    app.config.globalProperties.$auth = useAuth0(options)
  },
}
